// ============================================================
//  Formulaire création/édition d'une question (modale)
// ============================================================

import { useState, type FormEvent } from "react";
import { NIVEAUX, type Question, type QuestionInput } from "../lib/types";

export function QuestionForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: Question | null;
  onSubmit: (input: QuestionInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [texte, setTexte] = useState(initial?.texte ?? "");
  const [reponses, setReponses] = useState<string[]>(initial?.reponses ?? [""]);
  const [indice, setIndice] = useState(initial?.indice ?? "");
  const [matiere, setMatiere] = useState(initial?.matiere ?? "");
  const [niveau, setNiveau] = useState(initial?.niveau ?? "");
  const [difficulte, setDifficulte] = useState(initial?.difficulte ?? 1);
  const [emoji, setEmoji] = useState(initial?.emoji ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function setReponse(i: number, val: string) {
    setReponses((rs) => rs.map((r, j) => (j === i ? val : r)));
  }
  function addReponse() {
    setReponses((rs) => [...rs, ""]);
  }
  function removeReponse(i: number) {
    setReponses((rs) => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        texte: texte.trim(),
        reponses: reponses.map((r) => r.trim()).filter(Boolean),
        indice: indice.trim() || null,
        matiere: matiere.trim() || null,
        niveau: niveau || null,
        difficulte,
        emoji: emoji.trim() || null,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-form" onClick={(e) => e.stopPropagation()}>
        <h2>{initial ? "Modifier la question" : "Nouvelle question"}</h2>
        <form onSubmit={handleSubmit}>
          <label>
            <span>Question *</span>
            <textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              required
              rows={3}
              placeholder="Ex : Quelle est la capitale de la France ?"
              maxLength={1000}
              autoFocus
            />
          </label>

          <label>
            <span>Réponses acceptées * <small>(plusieurs variantes possibles)</small></span>
            <div className="reponses-list">
              {reponses.map((r, i) => (
                <div key={i} className="reponse-row">
                  <input
                    type="text"
                    value={r}
                    onChange={(e) => setReponse(i, e.target.value)}
                    placeholder={i === 0 ? "Ex : paris" : "Variante…"}
                    maxLength={200}
                  />
                  {reponses.length > 1 && (
                    <button type="button" onClick={() => removeReponse(i)} className="btn-remove">×</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addReponse} className="btn-add">+ Ajouter une variante</button>
            </div>
          </label>

          <div className="form-row">
            <label>
              <span>Niveau</span>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
                <option value="">—</option>
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label>
              <span>Matière</span>
              <input
                type="text"
                value={matiere}
                onChange={(e) => setMatiere(e.target.value)}
                placeholder="Ex : Maths"
                maxLength={100}
              />
            </label>
            <label>
              <span>Difficulté</span>
              <select value={difficulte} onChange={(e) => setDifficulte(parseInt(e.target.value, 10))}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{"●".repeat(n)}{"○".repeat(5 - n)} ({n}/5)</option>
                ))}
              </select>
            </label>
            <label>
              <span>Emoji</span>
              <input
                type="text"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="🇫🇷"
                maxLength={12}
              />
            </label>
          </div>

          <label>
            <span>Indice <small>(optionnel)</small></span>
            <input
              type="text"
              value={indice}
              onChange={(e) => setIndice(e.target.value)}
              placeholder="Astuce affichée si le joueur clique sur 💡"
              maxLength={500}
            />
          </label>

          {error && <p className="form-error">✗ {error}</p>}

          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? "Enregistrement…" : (initial ? "Enregistrer" : "Créer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
